/**
 *
 * SelectStyled
 *
 */
import React, { memo } from 'react';
import { components } from 'react-select';
import makeAnimated from 'react-select/animated';
import AsyncSelect from 'react-select/async';
import Select from 'react-select';

import { SelectWrapper, LabelWrapper, FieldWrapper, DescriptionWrapper, OptionalWrapper } from './styles';
import { Div } from 'styles/__layout';

export type DropDownItem = { value: string; label: string };

export interface Props {
  variant: string;
  options?: any[] | undefined;
  isGrouped?: boolean;
  isMulti?: boolean;
  isSearchable?: boolean;
  isClearable?: boolean;
  limitOnMulti?: number;
  value: string | DropDownItem[] | null | undefined;
  onChange: (e: DropDownItem) => void;
  placeholder?: string;
  hideIndicator?: boolean;
  label?: string | React.ReactElement;
  labelColor?: string;
  labelWeight?: number;
  description?: string;
  mr?: number | number[];
  ml?: number | number[] | string;
  mt?: number | number[] | string;
  mb?: number | number[];
  async?: boolean;
  ref?: any;
  disabled?: boolean;
  alignRight?: boolean;
  dropdownWidth?: string;
  height?: string;
  minHeight?: string;
  [x: string]: any;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  onMenuOpen?: () => void;
  onMenuClose?: () => void;
  loadOptions?: (input: string) => Promise<{ value: string; label: string }[]>;
  formatOptionLabel?: (option: any) => React.ReactNode;
  optional?: boolean;
}

export const SelectStyled = memo(
  ({
    variant,
    options,
    isMulti,
    isGrouped = false,
    isClearable,
    isSearchable,
    limitOnMulti,
    value,
    onChange,
    placeholder,
    hideIndicator,
    label,
    labelColor = 'blacks.1',
    labelWeight = 2,
    description,
    mr,
    ml,
    mt,
    mb,
    width,
    onMenuOpen,
    onMenuOpenClose,
    height,
    minHeight,
    loadOptions,
    async = false,
    ref,
    disabled,
    alignRight = false,
    dropdownWidth,
    menuPlacement = 'auto',
    formatOptionLabel,
    optional = false,
    ...props
  }: Props) => {
    /**
     * Functions
     */
    const renderLabel = () =>
      label && (
        <Div display="flex" flexDirection="row" alignItems="center" justifyContent="space-between" flexWrap="noWrap">
          <Div display="flex" flexDirection="column">
            <LabelWrapper fontSize={1} mb={description ? 0 : 3} fontWeight={labelWeight} color={labelColor}>
              {label}
            </LabelWrapper>
            {description && (
              <DescriptionWrapper color="grays.0" fontSize={0} mb={2}>
                {description}
              </DescriptionWrapper>
            )}
          </Div>
          {optional && <OptionalWrapper>(optional)</OptionalWrapper>}
        </Div>
      );

    //when dropdown is not manually clicked then it does not scroll into view
    const customizedOnMenuOpen = () => {
      onMenuOpenClose && onMenuOpenClose(true);
      setTimeout(() => {
        const selectedEl = document.getElementsByClassName('styled-select__option--is-selected')[0];
        if (selectedEl) {
          selectedEl.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
        }
      }, 0);
    };

    const customizedOnMenuClose = () => {
      onMenuOpenClose && setTimeout(() => onMenuOpenClose(false), 100);
    };

    const isOptionDisabledHandler = () => {
      const shouldDisableOption = limitOnMulti && isMulti && value && value?.length >= limitOnMulti;

      if (shouldDisableOption) {
        return true;
      }
      return false;
    };

    const isOptionDisabledProp = isMulti && limitOnMulti ? isOptionDisabledHandler : undefined;

    /**
     * Custom Components
     */
    const CustomOption = ({ children, ...props }) => {
      const { innerProps } = props;
      const { onClick } = innerProps;

      props.innerProps.onClick = e => {
        e && e.stopPropagation();
        e && e.preventDefault();
        onClick && onClick(e);
      };

      // @ts-ignore
      return <components.Option {...props}>{children}</components.Option>;
    };

    const animatedComponents = makeAnimated({ Option: CustomOption });

    /**
     * Return
     */
    return (
      <FieldWrapper
        mt={mt}
        mb={mb}
        ml={ml}
        mr={mr}
        display="flex"
        flexDirection="column"
        width={width}
        data-testid={props['data-testid']}
      >
        {renderLabel()}
        <SelectWrapper
          variant={variant}
          as={async ? AsyncSelect : Select}
          ref={ref}
          loadOptions={loadOptions}
          color="blacks.1"
          menuPortalTarget={document.body}
          menuPlacement={menuPlacement}
          height={height}
          borderRadius="0"
          fontSize={1}
          isDisabled={disabled}
          isOptionDisabled={isOptionDisabledProp}
          fontWeight={1}
          options={
            options &&
            (isGrouped || formatOptionLabel
              ? options
              : options.map(option => {
                  return {
                    value: option.value,
                    label: option.label ? option.label : option.value,
                  };
                }))
          }
          defaultValue={
            value && typeof value !== 'string'
              ? value
              : options &&
                options
                  .filter(option => option.value === value)
                  .map(option => {
                    return {
                      value: option.value,
                      label: option.label ? option.label : option.value,
                    };
                  })
          }
          value={
            value && typeof value !== 'string'
              ? value
              : options &&
                options
                  .filter(option => option.value === value)
                  .map(option => {
                    return {
                      value: option.value,
                      label: option.label ? option.label : option.value,
                    };
                  })
          }
          isMulti={isMulti}
          hideIndicator={hideIndicator}
          isSearchable={isSearchable}
          isClearable={isClearable}
          formatOptionLabel={formatOptionLabel}
          components={animatedComponents}
          menuShouldScrollIntoView={true}
          styles={{
            option: (provided, state) => ({
              ...provided,
              fontSize: '14px',
            }),
            menu: menu =>
              menuPlacement !== 'top'
                ? {
                    position: 'absolute',
                    background: 'white',
                    zIndex: '99999',
                    top: '6px',
                    ...(alignRight && { right: 0 }),
                    minWidth: dropdownWidth ?? '100%',
                    ...menu,
                    borderRadius: '12px',
                  }
                : {
                    zIndex: '99999',
                    ...menu,
                    borderRadius: '12px',
                  },
            control: provided => ({
              ...provided,
              background: '#fff',
              borderColor: '#9e9e9e',
              minHeight: minHeight ?? '36px',
              height: height ?? '36px',
            }),
            valueContainer: provided => ({
              ...provided,
              height: isMulti ? 'fit-content' : '30px',
              overflowY: 'scroll',
            }),
            input: provided => ({
              ...provided,
              margin: '0px',
            }),
            indicatorSeparator: () => ({
              display: 'none',
            }),
            indicatorsContainer: provided => ({
              ...provided,
              height: '30px',
            }),
            menuPortal: base => ({ ...base, zIndex: 9999 }),
          }}
          onChange={e => {
            onChange(e);
          }}
          onMenuOpen={customizedOnMenuOpen}
          onMenuClose={customizedOnMenuClose}
          className={'styled-select'}
          classNamePrefix="styled-select"
          placeholder={placeholder}
          {...props}
        />
      </FieldWrapper>
    );
  },
);
